function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import debounce from 'lodash/debounce';
import { jsx, ParamComponent } from 'panel';
import { NoExtension, Section, SectionItem } from 'common/components/items-menu/types';
import { SvgIconEl } from 'common/components/svg-icon';
import { TooltipEl } from 'common/components/tooltip';
import { Placement } from 'common/components/tooltip/types';
import { reporter } from 'common/report/error-reporting';
import { getProjectId } from 'common/report/util';
import { useTemplateSearch } from 'common/report/util/feature-sampling';
import { DEFAULT_SEARCH_QUERY_STRING, getBrowseUrl, getDisplayContext, getSearchContexts, getSearchIconFromDisplayContext, isBrowseUrl, searchUtils } from 'common/report/util/search';
import { ContentDiscoveryType, getSearchCommonTrackingProperties, getTrackingPropertiesForResult, getTrackingPropertiesForResultsLoad, SearchSource, SearchTrackedProperty, TypeaheadTrackedEvent } from 'common/report/util/search/tracking';
import { RecentSearchItemType, SearchDisplayContextTranslation, SearchQueryFilters, SearchQuerySort, SearchResult, SearchSortKey, SearchSortOrder } from 'common/report/util/search/types';
import { colors } from 'common/util/colors';
import { defineAndMakeJsxFactory } from 'common/util/jsx';
import { truncate } from 'common/util/string';
import { MixpanelUserItem } from 'common/widgets/user-list/types';
import { UserType } from 'common/widgets/user-list/constants';
import { UserListEl } from 'common/widgets/user-list';
import { IconSizeType } from 'common/constants';
import { ResultDisplayMode, SearchInputEl } from 'common/widgets/search-input';
import { InputTheme } from 'common/components/input/types';
import { IronContextRegistry } from 'common/report/context-registry';
import { TopCreator } from 'common/report/queries/app-api/project-top-creators';
import { fuzzyMatch } from 'common/util/fuzzy-match';
import { UserIconEl } from 'common/widgets/user-icon';
import { GlobalSearchController } from 'common/report/model-resources/global-search-controller';
import { Translatable } from 'common/i18n';
import { panel2react } from 'common/util/react';
const MAX_CREATORS_SHOWN = 2;
const MAX_RESULTS_SHOWN = 3;
const CREATOR_SEARCH_MIN_LENGTH = 3;
type GlobalSearchParams = {
  // TODO(global-sidenav): remove after GA
  currentUrlPath?: string;
  isModalMode?: boolean;
  onCompleteSearch?: () => void;
};
type GlobalSearchJsxProps = {
  params: GlobalSearchParams;
};
enum GlobalSearchItemType {
  Creator = "creator",
  Recent = "recent",
  Entity = "entity",
  BrowseAll = "browse-all",
  ReadOnly = "read-only",
}
type CreatorSearchSectionItem = SectionItem<TopCreator, {
  type: GlobalSearchItemType.Creator;
}>;
type EntitySearchSectionItem = SectionItem<SearchResult, {
  type: GlobalSearchItemType.Entity;
}>;
type BrowseAllSearchSectionItem = SectionItem<string, {
  type: GlobalSearchItemType.BrowseAll;
}>;
type ReadOnlySearchSectionItem = SectionItem<string, {
  type: GlobalSearchItemType.ReadOnly;
}>;
type SearchSectionItem = EntitySearchSectionItem | BrowseAllSearchSectionItem | ReadOnlySearchSectionItem | CreatorSearchSectionItem;
type SearchSection = Section<any, NoExtension, SearchSectionItem>;
interface GlobalSearchState {
  allowRecentItems?: boolean;
  errored?: boolean;
  open: boolean;
  loading?: boolean;
  searchedText?: string;
  searchResults: SearchResult[];
  searchInputEl?: HTMLElement;
}
function userListRenderer(item: EntitySearchSectionItem) {
  const users: MixpanelUserItem[] = [{
    name: item.value.user,
    id: item.value.user_id,
    email: item.value.email,
    type: UserType.Mixpanel
  }];
  return <UserListEl attrs={{
    users,
    'max-icons-displayed': 1,
    'user-count-override': item.value.unique_count,
    size: IconSizeType.Small
  }} data-sentry-element="UserListEl" data-sentry-component="userListRenderer" data-sentry-source-file="index.tsx" />;
}
function leftIconRenderer(_ref: {
  icon: string;
  tooltipText?: Translatable;
  isErrored?: boolean;
}) {
  let {
    icon,
    tooltipText,
    isErrored
  }: {
    icon: string;
    tooltipText?: Translatable;
    isErrored?: boolean;
  } = _ref;
  let color = null;
  if (isErrored) {
    color = colors.red1;
  }
  const iconComponent = <SvgIconEl attrs={{
    icon,
    color
  }} style={{
    margin: "8px"
  }} />;
  if (tooltipText) {
    return <div style={{
      display: "flex"
    }}>
        {iconComponent}
        <TooltipEl attrs={{
        placement: Placement.Top
      }}>{tooltipText}</TooltipEl>
      </div>;
  }
  return iconComponent;
}
export class GlobalSearch extends ParamComponent<GlobalSearchParams, GlobalSearchState, IronContextRegistry> {
  constructor() {
    var _this;
    super(...arguments);
    _this = this;
    _defineProperty(this, "resourceCustomSubscriptions", new Map([[GlobalSearchController, () => {
      this.setDefaultSearchText();
    }]]));
    _defineProperty(this, "executeSearch", debounce(async function (searchedText) {
      let sharedOnly = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      _this.update({
        searchResults: [],
        loading: true
      });
      const queryFilters: SearchQueryFilters = {
        email: [],
        type: getSearchContexts().map(value => ({
          value,
          negated: false
        }))
      };
      const trackingProps = {
        [SearchTrackedProperty.OwnerFilters]: [],
        [SearchTrackedProperty.ReportFilters]: [],
        [SearchTrackedProperty.SearchQuery]: searchedText
      };
      try {
        const sortKey = _this.getSearchSortKey(searchedText);
        const querySort: SearchQuerySort = sortKey ? {
          key: sortKey,
          order: SearchSortOrder.Descending
        } : null;
        const searchResults = await searchUtils.executeSearch({
          queryFilters,
          querySort,
          searchedText,
          sharedOnly,
          limit: 100
        });
        if (searchedText === _this.state.searchedText && searchResults) {
          _this.update({
            loading: false,
            searchResults
          });
          _this.track(TypeaheadTrackedEvent.SearchResultLoaded, _objectSpread(_objectSpread({}, trackingProps), getTrackingPropertiesForResultsLoad(searchResults)));
        }
      } catch (err) {
        reporter.error(err);
        _this.update({
          errored: true,
          loading: false
        });
      }
    }, 200));
  }
  get config() {
    return {
      template: () => {
        return <SearchInputEl attrs={{
          elref: "searchInputCmp"
        }} params={_objectSpread({
          onEnterPressed: () => {
            this.track(TypeaheadTrackedEvent.ShowAllSelected, {
              'Pressed Enter': true
            });
            this.redirectToBrowsePage();
          },
          onItemClick: this.helpers.handleSearchSelection,
          onClearClick: () => {
            if (isBrowseUrl() && !this.params.isModalMode) {
              this.redirectToBrowsePage();
            }
          },
          onMenuOpen: () => {
            this.track(TypeaheadTrackedEvent.OpenTypeahead);
            this.handleSearchedTextUpdate(this.state.searchedText);
          },
          onSearchedTextUpdate: (searchedText, isSuggestedItem) => {
            if (isSuggestedItem) {
              this.track(TypeaheadTrackedEvent.RecentQuerySelected);
            }
            this.handleSearchedTextUpdate(searchedText);
          },
          onTrackEvent: (event, props) => this.track(event, props !== null && props !== void 0 ? props : {}),
          searchedText: this.state.searchedText,
          sections: this.getSearchSections(),
          onCompleteSearch: this.params.onCompleteSearch
        }, this.params.isModalMode ? {
          resultDisplayMode: ResultDisplayMode.List,
          theme: InputTheme.Default
        } : {
          enableShortcut: true,
          resultDisplayMode: ResultDisplayMode.DropMenu,
          theme: InputTheme.Chrome
        })} />;
      },
      contexts: ["currentProject" as const, "projectTopCreators" as const, "urlManager" as const, "globalSearchController" as const, "i18n" as const],
      defaultState: {
        allowRecentItems: true,
        errored: false,
        loading: false,
        searchResults: [],
        searchedText: DEFAULT_SEARCH_QUERY_STRING,
        open: false
      },
      params: {
        currentUrlPath: String,
        isModalMode: Boolean,
        onCompleteSearch: Function
      },
      helpers: {
        handleSearchSelection: (item: SearchSectionItem) => {
          if (item.type === GlobalSearchItemType.Entity) {
            this.update({
              searchResults: [],
              searchedText: ""
            });
            this.track(TypeaheadTrackedEvent.ResultSelected, getTrackingPropertiesForResult(item.value));
          } else if (item.type === GlobalSearchItemType.BrowseAll) {
            this.track(TypeaheadTrackedEvent.ShowAllSelected);
          } else if (item.type === GlobalSearchItemType.Creator) {
            this.update({
              searchResults: [],
              searchedText: ""
            });
            const recentItem = {
              id: item.value.id,
              userEmail: item.value.email,
              key: "user-".concat(item.value.id),
              label: item.value.name,
              type: RecentSearchItemType.User
            };
            searchUtils.addRecentItem(recentItem);
            this.track(TypeaheadTrackedEvent.CreatorFilterSelected);
          }
        }
      },
      hooks: {
        preUpdate: (_newState, newParams?: GlobalSearchParams) => {
          if (newParams && newParams.currentUrlPath !== this.params.currentUrlPath) {
            this.setDefaultSearchText();
          }
        }
      }
    };
  }
  connectedCallback(): void {
    super.connectedCallback();
    this.setDefaultSearchText();
    if (this.params.isModalMode) {
      this.handleSearchedTextUpdate(this.state.searchedText);
    }
  }
  get t() {
    return this.getContext("i18n").t;
  }
  getSearchParamsContext() {
    return this.getContext("globalSearchController");
  }
  setDefaultSearchText() {
    const persistedSearchedText = this.getSearchParamsContext().getSearchedText();
    if (isBrowseUrl()) {
      if (persistedSearchedText && persistedSearchedText !== this.state.searchedText) {
        this.update({
          searchedText: persistedSearchedText
        });
      }
    } else {
      this.update({
        searchedText: ""
      });
    }
  }
  handleSearchedTextUpdate(searchedText: string): void {
    if (this.state.searchedText !== searchedText || !this.state.searchResults.length) {
      this.update({
        searchedText,
        loading: true
      });
      this.executeSearch(searchedText, true);
    }
  }
  redirectToBrowsePage() {
    this.getUrlManager().setLocation(getBrowseUrl({
      queryParam: {
        q: this.state.searchedText || ""
      }
    }));
  }
  private getUrlManager() {
    return this.getContext("urlManager");
  }
  getSearchSections(): SearchSection[] {
    const shouldLinkSearchedText = this.state.searchedText !== DEFAULT_SEARCH_QUERY_STRING;
    return [...this.getSearchCreatorSection(), ...this.getSearchResultSections(), ...(useTemplateSearch() ? this.getTemplateSearchResultSections() : []), ...(shouldLinkSearchedText ? [this.getBrowseItem()] : [])];
  }
  private getBrowseItem(): SearchSection {
    const href = getBrowseUrl({
      queryParam: {
        q: this.state.searchedText || ""
      }
    });
    return {
      isLoading: false,
      iconRenderer: () => leftIconRenderer({
        icon: "arrow-diagonal"
      }),
      items: [{
        label: this.t("search.show-all-results"),
        type: GlobalSearchItemType.BrowseAll,
        href
      }]
    };
  }
  private getSearchSortKey(searchedText: string): SearchSortKey | null {
    if (searchedText === DEFAULT_SEARCH_QUERY_STRING) {
      if (this.getContext("projectTopCreators").hasViewedContent()) {
        return SearchSortKey.RecentlyViewed;
      } else {
        return SearchSortKey.Popularity;
      }
    }
    return null;
  }
  private getTemplateSearchResultSections(): SearchSection[] {
    const {
      loading,
      searchedText
    } = this.state;
    const items = [...searchUtils.getTemplateItems(searchedText, this.getContext("currentProject").get().value)];
    return items.length === 0 || loading ? [] : this.processSearchResultItems({
      items,
      label: this.t("search.templates"),
      loading,
      maxSearchResults: items.length
    });
  }
  private getSearchResultSections(): SearchSection[] {
    var _SearchSortKey$Popula;
    const {
      loading,
      searchResults,
      searchedText
    } = this.state;
    const items = searchResults.map((item: SearchResult): EntitySearchSectionItem => {
      return {
        href: item.link,
        icon: getSearchIconFromDisplayContext(getDisplayContext(item)),
        label: truncate(item.name, 50),
        subLabel: item.dashboard,
        type: GlobalSearchItemType.Entity,
        value: item
      };
    });
    const label = (_SearchSortKey$Popula = {
      [SearchSortKey.Popularity]: this.t("search.most-viewed"),
      [SearchSortKey.RecentlyViewed]: this.t("search.recently-viewed")
    }[this.getSearchSortKey(searchedText)]) !== null && _SearchSortKey$Popula !== void 0 ? _SearchSortKey$Popula : this.t("search.boards-and-reports");
    return this.processSearchResultItems({
      items,
      label,
      loading,
      maxSearchResults: MAX_RESULTS_SHOWN
    });
  }
  private processSearchResultItems(_ref2): SearchSection[] {
    let {
      items,
      label,
      loading,
      maxSearchResults
    } = _ref2;
    const {
      errored
    } = this.state;
    const results: SearchSection = {
      label: loading ? null : label,
      isLoading: loading,
      hideTopDivider: true,
      actionsRenderer: (item: EntitySearchSectionItem) => !errored && items.length > 0 ? userListRenderer(item) : null,
      iconRenderer: (item: SearchSectionItem) => {
        if (item.type === GlobalSearchItemType.Entity) {
          return leftIconRenderer({
            icon: item.icon,
            tooltipText: SearchDisplayContextTranslation[item.value.report],
            isErrored: errored
          });
        }
        return null;
      },
      items: items.length > 0 ? items.slice(0, maxSearchResults) : [{
        icon: errored ? "warning" : "search",
        isReadOnly: true,
        label: errored ? this.t("search.error") : this.t("search.no-items-matched"),
        subLabel: errored ? this.t("search.try-again") : null,
        type: GlobalSearchItemType.ReadOnly
      }]
    };
    return [results];
  }
  private getSearchCreatorSection(): SearchSection[] {
    var _this$state$searchedT;
    const creators = this.getContext("projectTopCreators").getCreators();
    if (((_this$state$searchedT = this.state.searchedText) === null || _this$state$searchedT === void 0 ? void 0 : _this$state$searchedT.length) < CREATOR_SEARCH_MIN_LENGTH || !creators) {
      return [];
    }
    const rank = (creator: TopCreator) => {
      var _fuzzyMatch$score, _fuzzyMatch;
      return (_fuzzyMatch$score = (_fuzzyMatch = fuzzyMatch(creator.name, this.state.searchedText)) === null || _fuzzyMatch === void 0 ? void 0 : _fuzzyMatch.score) !== null && _fuzzyMatch$score !== void 0 ? _fuzzyMatch$score : 0;
    };
    const rankedCreators = creators.filter(creator => rank(creator) > 0).sort((creator1, creator2) => rank(creator2) - rank(creator1)).slice(0, MAX_CREATORS_SHOWN);
    const items: CreatorSearchSectionItem[] = rankedCreators.map((creator: TopCreator) => {
      return {
        label: creator.name,
        type: GlobalSearchItemType.Creator,
        value: creator,
        href: getBrowseUrl({
          queryParam: {
            email: creator.email
          }
        })
      };
    });
    return [{
      label: "Creators",
      items,
      iconRenderer: (item: CreatorSearchSectionItem) => {
        return <UserIconEl attrs={{
          id: item.value.id,
          email: item.value.email,
          name: item.value.name,
          size: IconSizeType.Small
        }} />;
      }
    }];
  }
  private track(eventName: string) {
    let props: Obj = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    mixpanel.track(eventName, _objectSpread(_objectSpread(_objectSpread({}, this.getCommonTrackingProperties()), props), {}, {
      project_id: getProjectId()
    }));
  }
  private getCommonTrackingProperties() {
    return _objectSpread(_objectSpread({}, getSearchCommonTrackingProperties()), {}, {
      [SearchTrackedProperty.Source]: SearchSource.Dropdown,
      [SearchTrackedProperty.ContentDiscoveryType]: this.state.searchedText ? ContentDiscoveryType.SearchQuery : ContentDiscoveryType.Browse
    });
  }
}
export const GlobalSearchEl = defineAndMakeJsxFactory<GlobalSearchJsxProps>("mp-global-search", GlobalSearch);
export const GlobalSearchReact = panel2react<GlobalSearchJsxProps>("mp-global-search", GlobalSearch);